/**
 * GroupsView - Admin-only group management page
 * Desktop only, appears as a tab in the left sidebar under Messages.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useDesign } from '@/contexts/DesignContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { UserAvatar } from '@/components/shared';
import { toast } from 'sonner';
import { 
  Check,
  Plus, Users, Edit, Trash2, UserPlus, UserMinus, X, 
  ArrowLeft, Play, Tag, ChevronRight, AlertCircle, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TAG_SUGGESTIONS = ['youth-band', 'production', 'guitarists', 'vocalists', 'sound-team', 'media-team'];

/* Studio (119 §21, mock frame 2): the same five idea groups the classic empty
   state lists, as data, so they can be drawn as one 3-column grid of card2
   insets. Inert content in both looks — they do nothing when clicked. */
const IDEA_GROUPS = [
  ['By ministry', ["Men's Team", 'Youth Band']],
  ['By general groups', ['Tech Team', 'Vocalists', 'Musicians', 'Worship Leaders']],
  ['By instrument', ['Guitarists', 'Drummers']],
  ['By sub-category', ['Lighting', 'Audio']],
  ['By multi-site campus', ['Campus 1', 'Campus 2']],
];

/* Studio skeleton pieces (mock frames 3, 4, 6) — the classic look keeps its
   spinner and its three pulsing blocks. */
const StSkel = ({ w, h, r = 6 }) => (
  <span className="st-skel" style={{ width: w, height: h, borderRadius: r }} aria-hidden="true" />
);
const StSkelRow = ({ w }) => (
  <div className="st-skrow">
    <StSkel w="32px" h={32} r={9} />
    <div className="st-skcol"><StSkel w={w} h={14} /><StSkel w="38%" h={12} /></div>
  </div>
);

export default function GroupsView({ user }) {
  const { isStudio } = useDesign();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupDetail, setGroupDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Create/Edit dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupName, setGroupName] = useState('');
  const [groupTag, setGroupTag] = useState('');
  const [tagError, setTagError] = useState('');
  const [saving, setSaving] = useState(false);

  // Add members dialog
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [churchMembers, setChurchMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [selectedNewMembers, setSelectedNewMembers] = useState([]);
  const [addingMembers, setAddingMembers] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false); // roster fetch in flight (R33)

  // Delete confirmation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState(null);

  // Intro video
  const [showIntroVideo, setShowIntroVideo] = useState(false);
  const [introVideoDismissed, setIntroVideoDismissed] = useState(true);

  // Fetch groups
  const fetchGroups = useCallback(async () => {
    if (!user?.church_id) return;
    try {
      const res = await fetch(`${API_URL}/api/groups?church_id=${user.church_id}`);
      if (res.ok) {
        setGroups(await res.json());
      }
    } catch (err) {
      console.error('Error fetching groups:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.church_id]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  // Check intro dismissed
  useEffect(() => {
    const checkIntro = async () => {
      if (!user?.id) return;
      try {
        const res = await fetch(`${API_URL}/api/auth/user-preferences/${user.id}`);
        if (res.ok) {
          const data = await res.json();
          setIntroVideoDismissed(data.groups_intro_dismissed === true);
        } else {
          setIntroVideoDismissed(false);
        }
      } catch { setIntroVideoDismissed(false); }
    };
    checkIntro();
  }, [user?.id]);

  const handleDismissIntro = useCallback(async () => {
    setShowIntroVideo(false);
    setIntroVideoDismissed(true);
    if (!user?.id) return;
    try {
      await fetch(`${API_URL}/api/auth/user-preferences/${user.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ groups_intro_dismissed: true, email: user.email })
      });
    } catch (err) { console.error('Error saving intro dismissed:', err); }
  }, [user?.id, user?.email]);

  // NOTE: No custom ESC handler here — shadcn <Dialog> wraps Radix UI Dialog
  // which has built-in ESC handling via `onOpenChange`. Adding a window-level
  // keydown listener would cause handleDismissIntro to fire twice per ESC.

  // Tag validation
  const validateTag = (val) => {
    const t = val.toLowerCase().replace(/^@/, '').replace(/\s+/g, '-');
    setGroupTag(t);
    if (/[^a-z0-9-]/.test(t)) {
      setTagError('Only lowercase letters, numbers, and hyphens');
    } else if (t.length > 30) {
      setTagError('Max 30 characters');
    } else {
      setTagError('');
    }
  };

  // Create or update group
  const handleSaveGroup = async () => {
    if (!groupName.trim() || !groupTag.trim() || tagError) return;
    setSaving(true);
    try {
      const tag = groupTag.toLowerCase().replace(/^@/, '');
      if (editingGroup) {
        const res = await fetch(`${API_URL}/api/groups/${editingGroup.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: user.id, name: groupName.trim(), tag })
        });
        if (!res.ok) {
          const err = await res.json();
          toast.error(err.detail || 'Failed to update group');
          return;
        }
        toast.success('Group updated');
      } else {
        const res = await fetch(`${API_URL}/api/groups`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ church_id: user.church_id, user_id: user.id, name: groupName.trim(), tag })
        });
        if (!res.ok) {
          const err = await res.json();
          toast.error(err.detail || 'Failed to create group');
          return;
        }
        const newGroup = await res.json();
        toast.success('Group created');

        // Close create dialog and reset form
        setShowCreateDialog(false);
        setEditingGroup(null);
        setGroupName('');
        setGroupTag('');

        // Optimistically update groups list and select the new group
        setGroups(prev => [...prev, newGroup]);
        setSelectedGroup(newGroup.id);
        setGroupDetail({ ...newGroup, members: [] });

        // Auto-open Add Members dialog
        setSelectedNewMembers([]);
        setMemberSearch('');
        fetchChurchMembers();
        setShowAddMembers(true);
        return;
      }
      setShowCreateDialog(false);
      setEditingGroup(null);
      setGroupName('');
      setGroupTag('');
      fetchGroups();
      if (selectedGroup) {
        fetchGroupDetail(editingGroup?.id || selectedGroup);
      }
    } catch (err) {
      toast.error('Couldn\'t complete that action. Please refresh and try again.');
    } finally {
      setSaving(false);
    }
  };

  // Fetch group detail
  const fetchGroupDetail = async (groupId) => {
    setLoadingDetail(true);
    try {
      const res = await fetch(`${API_URL}/api/groups/${groupId}?user_id=${encodeURIComponent(user?.id || '')}`);
      if (res.ok) {
        setGroupDetail(await res.json());
      }
    } catch (err) {
      console.error('Error fetching group detail:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleSelectGroup = (group) => {
    setSelectedGroup(group.id);
    fetchGroupDetail(group.id);
  };

  // Fetch church members for add dialog
  const fetchChurchMembers = async () => {
    setLoadingMembers(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/church/${user.church_id}/members`);
      if (res.ok) {
        const data = await res.json();
        setChurchMembers(data.members || data || []);
      } else {
        toast.error('Could not load your team list');
      }
    } catch (err) {
      console.error('Error fetching church members:', err);
      toast.error('Could not load your team list');
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleOpenAddMembers = () => {
    setSelectedNewMembers([]);
    setMemberSearch('');
    fetchChurchMembers();
    setShowAddMembers(true);
  };

  const handleAddMembers = async () => {
    if (!selectedNewMembers.length || !selectedGroup) return;
    setAddingMembers(true);
    try {
      const res = await fetch(`${API_URL}/api/groups/${selectedGroup}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, member_ids: selectedNewMembers })
      });
      if (res.ok) {
        // Count from the server's answer, not the selection: the group may have
        // changed underneath us and the server de-duplicates.
        let added = selectedNewMembers.length;
        try {
          const updated = await res.json();
          // Count the people WE selected that are now in the group (another admin
          // may have added others in the meantime).
          if (Array.isArray(updated?.members)) {
            added = updated.members.filter((id) => selectedNewMembers.includes(id) && !existingMemberIds.has(id)).length;
          }
        } catch { /* keep the selection count */ }
        toast.success(`Added ${added} member${added === 1 ? '' : 's'}`);
        setShowAddMembers(false);
        fetchGroupDetail(selectedGroup);
        fetchGroups();
      } else {
        // 403 (lost admin in another tab) / 404 (group deleted): say so and keep
        // the selection so nothing has to be redone.
        let detail = 'Failed to add members';
        try { detail = (await res.json())?.detail || detail; } catch { /* ignore */ }
        toast.error(detail);
      }
    } catch (err) {
      toast.error('Failed to add members');
    } finally {
      setAddingMembers(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!selectedGroup) return;
    try {
      const res = await fetch(`${API_URL}/api/groups/${selectedGroup}/members/${memberId}?user_id=${user.id}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        toast.success('Member removed');
        fetchGroupDetail(selectedGroup);
        fetchGroups();
      }
    } catch (err) {
      toast.error('Failed to remove member');
    }
  };

  const handleDeleteGroup = async () => {
    if (!deletingGroup) return;
    try {
      const res = await fetch(`${API_URL}/api/groups/${deletingGroup.id}?user_id=${user.id}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        toast.success('Group deleted');
        if (selectedGroup === deletingGroup.id) {
          setSelectedGroup(null);
          setGroupDetail(null);
        }
        setShowDeleteDialog(false);
        setDeletingGroup(null);
        fetchGroups();
      }
    } catch (err) {
      toast.error('Failed to delete group');
    }
  };

  const openEditDialog = (group) => {
    setEditingGroup(group);
    setGroupName(group.name);
    setGroupTag(group.tag);
    setTagError('');
    setShowCreateDialog(true);
  };

  const openCreateDialog = () => {
    setEditingGroup(null);
    setGroupName('');
    setGroupTag('');
    setTagError('');
    setShowCreateDialog(true);
  };

  const existingMemberIds = new Set(groupDetail?.members || []);
  const filteredChurchMembers = churchMembers.filter(m => {
    if (existingMemberIds.has(m.user_id)) return false;
    if (!memberSearch) return true;
    const q = memberSearch.toLowerCase();
    return (m.user_name || m.display_name || '').toLowerCase().includes(q) || (m.user_email || '').toLowerCase().includes(q);
  });

  /* ---- Studio (119 §21) render pieces. Every handler is the classic one. ---- */
  const stListHeader = () => (
    <div className="st-glh">
      <h2 className="st-gt20">Groups</h2>
      {!introVideoDismissed && (
        <button
          type="button"
          className="st-btn st-ghost st-sm"
          onClick={() => setShowIntroVideo(true)}
          data-ui="howto"
          data-testid="groups-intro-btn"
        >
          <Play />
          <span>How it works</span>
        </button>
      )}
      <button type="button" className="st-btn st-pri st-sm" onClick={openCreateDialog} data-testid="create-group-btn">
        <Plus />New
      </button>
    </div>
  );

  const stDetailSkeletonBody = () => (
    <>
      <div className="st-gdh">
        <StSkel w="44px" h={44} r={9} />
        <div className="st-skcol"><StSkel w="120px" h={16} /><StSkel w="180px" h={12} /></div>
      </div>
      <div className="st-gmh"><StSkel w="70px" h={14} /></div>
      <div className="st-gtblwrap">
        {[1, 2, 3, 4].map(i => (
          <div className="st-skrow" key={i} style={{ padding: '10px 0' }}>
            <StSkel w="28px" h={28} r={14} />
            <div className="st-skcol"><StSkel w="34%" h={13} /><StSkel w="48%" h={11} /></div>
            <StSkel w="56px" h={13} />
          </div>
        ))}
      </div>
    </>
  );
  const stDetailSkeleton = () => <div className="st-gdet">{stDetailSkeletonBody()}</div>;

  // Loading state — prevent sidebar flash before we know if groups exist
  if (loading) {
    if (isStudio) {
      return (
        <div className="st-gwrap">
          <div className="st-glist">
            {stListHeader()}
            <div className="st-glb" style={{ gap: 0 }}>
              {['60%', '44%', '52%', '70%'].map(w => <StSkelRow w={w} key={w} />)}
            </div>
          </div>
          {stDetailSkeleton()}
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center h-[calc(100vh-57px)]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 dark:border-white/10 border-t-slate-600" />
      </div>
    );
  }

  // Empty state
  if (groups.length === 0 && isStudio) {
    /* Studio (mock frame 2): one card holding the empty shape, then the eyebrow
       and the five idea groups as card2 insets of plain chips. The classic title
       and sentence are kept verbatim; both buttons keep their testids. */
    return (
      <div className="st-gempty" data-testid="groups-empty-state">
        <div className="st-gemptyin">
          <div className="st-empty" style={{ padding: 0 }}>
            <span className="st-glyph"><Users /></span>
            <b>Create your first group</b>
            <span>Groups let you send targeted posts and notifications to specific team members.</span>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              {!introVideoDismissed && (
                <button
                  type="button"
                  className="st-btn st-ghost"
                  onClick={() => setShowIntroVideo(true)}
                  data-ui="howto"
                  data-testid="groups-intro-video-btn"
                >
                  <Play /><span>How it works</span>
                </button>
              )}
              <button type="button" className="st-btn st-pri" onClick={openCreateDialog} data-testid="create-first-group-btn">
                <Plus />Create group
              </button>
            </div>
          </div>
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
            <div className="st-geyebrow">Ideas to get started</div>
            <div className="st-gideas">
              {IDEA_GROUPS.map(([label, chips]) => (
                <div className="st-gidea" key={label}>
                  <b>{label}</b>
                  <div>{chips.map(c => <span className="st-gchip" key={c}>{c}</span>)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {showIntroVideo && (
          <Dialog open={showIntroVideo} onOpenChange={handleDismissIntro}>
            <DialogContent className="max-w-3xl p-0 overflow-hidden" data-ui-dialog="loom">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.loom.com/embed/39bd17e044e54c80a3f8c02e778e73e9"
                  title="Groups Instructions"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              <div className="px-4 py-2 bg-slate-50 dark:bg-[#1e1f22] border-t border-slate-200 dark:border-white/10 text-right">
                <a
                  href="https://www.loom.com/share/39bd17e044e54c80a3f8c02e778e73e9"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                  data-testid="groups-intro-fallback-1"
                >
                  <ExternalLink className="w-3 h-3" />
                  Trouble playing? Open in Loom
                </a>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {renderCreateDialog()}
        {renderAddMembersDialog()}
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="flex flex-col items-center h-[calc(100vh-120px)] px-6 overflow-y-auto" data-testid="groups-empty-state">
        <div className="flex flex-col items-center pt-12 pb-2">
          <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-[#3a3d41] flex items-center justify-center mb-4">
            <Users className="w-8 h-8 text-slate-400 dark:text-slate-500" />
          </div>
          <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-2">Create your first group</h2>
          <p className="text-slate-500 dark:text-slate-400 text-center text-sm max-w-sm mb-5">
            Groups let you send targeted posts and notifications to specific team members.
          </p>

          {/* Watch Instructions — disappears after first play */}
          {!introVideoDismissed && (
            <button
              onClick={() => setShowIntroVideo(true)}
              className="relative px-4 py-2 rounded-lg bg-orange-500 text-white text-sm font-medium flex items-center gap-2 mb-3 overflow-hidden"
              style={{ isolation: 'isolate' }}
              data-ui="howto" data-testid="groups-intro-video-btn"
            >
              <span 
                className="absolute inset-0 rounded-lg"
                style={{
                  background: `conic-gradient(from var(--angle), transparent 60%, #faff00 80%, transparent 100%)`,
                  animation: 'spin-snake 3s linear infinite',
                  padding: '2px',
                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  WebkitMaskComposite: 'xor',
                  maskComposite: 'exclude',
                }}
              />
              <Play className="w-4 h-4" />
              <span>{isStudio ? 'How it works' : 'Watch Instructions'}</span>
            </button>
          )}

          <Button onClick={openCreateDialog} className="gap-2" data-testid="create-first-group-btn">
            <Plus className="w-4 h-4" />
            Create Group
          </Button>
        </div>

        {/* Category suggestions */}
        <div className="w-full max-w-md mt-6 mb-8">
          <p className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider text-center mb-3">Ideas to get started</p>

          {/* Top section — two stacked left, one tall right */}
          <div className="grid grid-cols-2 grid-rows-2 gap-2.5">
            <div className="border border-slate-200 dark:border-white/10 rounded-lg p-3 bg-card">
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">By ministry</p>
              <div className="flex flex-wrap gap-1.5">
                <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 dark:bg-[#3a3d41] text-slate-500 dark:text-slate-400">Men's Team</span>
                <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 dark:bg-[#3a3d41] text-slate-500 dark:text-slate-400">Youth Band</span>
              </div>
            </div>
            <div className="border border-slate-200 dark:border-white/10 rounded-lg p-3 bg-card row-span-2">
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">By general groups</p>
              <div className="flex flex-wrap gap-1.5">
                <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 dark:bg-[#3a3d41] text-slate-500 dark:text-slate-400">Tech Team</span>
                <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 dark:bg-[#3a3d41] text-slate-500 dark:text-slate-400">Vocalists</span>
                <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 dark:bg-[#3a3d41] text-slate-500 dark:text-slate-400">Musicians</span>
                <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 dark:bg-[#3a3d41] text-slate-500 dark:text-slate-400">Worship Leaders</span>
              </div>
            </div>
            <div className="border border-slate-200 dark:border-white/10 rounded-lg p-3 bg-card">
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">By instrument</p>
              <div className="flex flex-wrap gap-1.5">
                <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 dark:bg-[#3a3d41] text-slate-500 dark:text-slate-400">Guitarists</span>
                <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 dark:bg-[#3a3d41] text-slate-500 dark:text-slate-400">Drummers</span>
              </div>
            </div>
          </div>

          {/* Bottom row */}
          <div className="grid grid-cols-2 gap-2.5 mt-2.5">
            <div className="border border-slate-200 dark:border-white/10 rounded-lg p-3 bg-card">
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">By sub-category</p>
              <div className="flex flex-wrap gap-1.5">
                <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 dark:bg-[#3a3d41] text-slate-500 dark:text-slate-400">Lighting</span>
                <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 dark:bg-[#3a3d41] text-slate-500 dark:text-slate-400">Audio</span>
              </div>
            </div>
            <div className="border border-slate-200 dark:border-white/10 rounded-lg p-3 bg-card">
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">By multi-site campus</p>
              <div className="flex flex-wrap gap-1.5">
                <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 dark:bg-[#3a3d41] text-slate-500 dark:text-slate-400">Campus 1</span>
                <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 dark:bg-[#3a3d41] text-slate-500 dark:text-slate-400">Campus 2</span>
              </div>
            </div>
          </div>
        </div>

        {/* Intro Video Dialog */}
        {showIntroVideo && (
          <Dialog open={showIntroVideo} onOpenChange={handleDismissIntro}>
            <DialogContent className="max-w-3xl p-0 overflow-hidden" data-ui-dialog={isStudio ? 'loom' : undefined}>
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.loom.com/embed/39bd17e044e54c80a3f8c02e778e73e9"
                  title="Groups Instructions"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              <div className="px-4 py-2 bg-slate-50 dark:bg-[#1e1f22] border-t border-slate-200 dark:border-white/10 text-right">
                <a
                  href="https://www.loom.com/share/39bd17e044e54c80a3f8c02e778e73e9"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                  data-testid="groups-intro-fallback-1"
                >
                  <ExternalLink className="w-3 h-3" />
                  Trouble playing? Open in Loom
                </a>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {renderCreateDialog()}
        {renderAddMembersDialog()}
      </div>
    );
  }

  function renderCreateDialog() {
    return (
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md" data-testid="create-group-dialog">
          <DialogHeader>
            <DialogTitle>
              {isStudio
                ? (editingGroup ? 'Edit group' : 'Create group')
                : (editingGroup ? 'Edit Group' : 'Create Group')}
            </DialogTitle>
          </DialogHeader>
          <div className={isStudio ? 'st-fldstack' : 'space-y-4 py-2'}>
            <div className={isStudio ? 'st-fld' : undefined}>
              <label className={isStudio ? 'st-fldlbl' : 'text-sm font-medium text-slate-700 dark:text-slate-200 mb-1 block'}>
                {isStudio ? 'Group name' : 'Group Name'}
              </label>
              <Input
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="e.g., Youth Band"
                data-testid="group-name-input"
              />
            </div>
            <div className={isStudio ? 'st-fld' : undefined}>
              <label className={isStudio ? 'st-fldlbl' : 'text-sm font-medium text-slate-700 dark:text-slate-200 mb-1 block'}>
                {isStudio ? '@tag name' : (
                  <span className="flex items-center gap-1.5">
                    <Tag className="w-3.5 h-3.5" />
                    Enter @tag name
                  </span>
                )}
              </label>
              <div className={isStudio ? 'relative st-gtagwrap' : 'relative'}>
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 text-sm">@</span>
                <Input
                  value={groupTag}
                  onChange={(e) => validateTag(e.target.value)}
                  placeholder="youth-band"
                  className={isStudio && tagError ? 'pl-7 st-inerr' : 'pl-7'}
                  data-testid="group-tag-input"
                />
              </div>
              {tagError && (
                isStudio ? (
                  <p className="st-gerr">{tagError}</p>
                ) : (
                  <p className="text-xs text-red-500 dark:text-red-400 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {tagError}
                  </p>
                )
              )}
              <p className={isStudio ? 'st-ghelp' : 'text-xs text-slate-400 dark:text-slate-500 mt-1.5'}>
                {isStudio
                  ? 'No spaces allowed. Use hyphens between words, like youth-band.'
                  : 'No spaces allowed. Use hyphens between words (e.g., youth-band)'}
              </p>
              {!groupTag && !editingGroup && (
                <div className={isStudio ? 'st-gchips' : 'flex flex-wrap gap-1.5 mt-2'}>
                  {TAG_SUGGESTIONS.filter(s => !groups.some(g => g.tag === s)).slice(0, 4).map(s => (
                    <button
                      key={s}
                      onClick={() => { setGroupTag(s); setTagError(''); }}
                      className={isStudio
                        ? 'st-gchip'
                        : "text-xs px-2 py-1 rounded-full bg-slate-100 dark:bg-[#3a3d41] text-slate-600 dark:text-slate-300 hover:bg-slate-200 transition-colors"}
                    >
                      @{s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter data-ui="dlg-f">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button
              onClick={handleSaveGroup}
              disabled={!groupName.trim() || !groupTag.trim() || !!tagError || saving}
              data-testid="save-group-btn"
            >
              {saving
                ? 'Saving...'
                : isStudio
                  ? (editingGroup ? 'Save changes' : 'Create group')
                  : (editingGroup ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  function renderAddMembersDialog() {
    const noAvailableMembers = !loadingMembers && filteredChurchMembers.length === 0 && !memberSearch;
    // R33 (2026-09-07): bulk add. Select-all acts on the rows currently shown
    // (so "search 'band' → select all → search 'tech' → select all → Add" works);
    // selections persist across searches; Clear drops everything.
    const selectedSet = new Set(selectedNewMembers);
    const visibleIds = filteredChurchMembers.map(m => m.user_id);
    const visibleSelected = visibleIds.filter(id => selectedSet.has(id)).length;
    const allVisibleSelected = visibleIds.length > 0 && visibleSelected === visibleIds.length;
    const selectAllState = allVisibleSelected ? true : visibleSelected > 0 ? 'indeterminate' : false;
    const toggleSelectAllVisible = () => {
      setSelectedNewMembers(prev => {
        if (allVisibleSelected) return prev.filter(id => !visibleIds.includes(id));
        const next = new Set(prev);
        visibleIds.forEach(id => next.add(id));
        return Array.from(next);
      });
    };
    const roleLabel = (role) => (role === 'owner' ? 'Owner' : role === 'admin' ? 'Admin' : null);
    return (
      <Dialog open={showAddMembers} onOpenChange={setShowAddMembers}>
        <DialogContent
          className={isStudio ? 'sm:max-w-lg' : 'sm:max-w-md'}
          data-ui-dialog={isStudio ? 'add-members' : undefined}
          data-testid="add-members-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>{isStudio ? 'Add members to' : 'Add Members to'} {groupDetail?.name || 'Group'}</span>
              {selectedNewMembers.length > 0 && (
                <Badge variant="secondary" className="font-medium" data-testid="selected-members-count">
                  {selectedNewMembers.length} selected
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          <Input
            value={memberSearch}
            onChange={(e) => setMemberSearch(e.target.value)}
            placeholder="Search team members..."
            className="mb-2"
            data-testid="member-search-input"
          />
          {!loadingMembers && visibleIds.length > 0 && (
            <div className={isStudio ? 'st-amsel' : 'flex items-center justify-between px-2 pb-1'}>
              <label className={isStudio ? undefined : 'flex items-center gap-2 text-xs font-medium text-slate-600 dark:text-slate-300 cursor-pointer select-none'}>
                <Checkbox
                  checked={selectAllState}
                  onCheckedChange={toggleSelectAllVisible}
                  aria-label={memberSearch ? `Select all ${visibleIds.length} matching` : 'Select all'}
                  data-testid="select-all-members"
                />
                {memberSearch
                  ? `Select all ${visibleIds.length} matching`
                  : (isStudio ? `Select all ${visibleIds.length}` : `Select all (${visibleIds.length})`)}
              </label>
              {selectedNewMembers.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedNewMembers([])}
                  className={isStudio
                    ? 'st-amclear'
                    : "text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 underline-offset-2 hover:underline"}
                  data-testid="clear-selected-members"
                >
                  Clear
                </button>
              )}
            </div>
          )}
          <ScrollArea className="max-h-64">
            <div className="space-y-1 px-1 py-1" role="group" aria-label="Team members">
              {loadingMembers && (
                isStudio ? (
                  <div data-testid="members-loading">
                    {['56%', '40%', '64%'].map(w => (
                      <div className="st-skrow" key={w} style={{ padding: '10px 8px' }}>
                        <StSkel w="18px" h={18} r={5} />
                        <StSkel w="28px" h={28} r={14} />
                        <div className="st-skcol"><StSkel w={w} h={13} /><StSkel w="44%" h={11} /></div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-sm text-slate-400 dark:text-slate-500 py-4" data-testid="members-loading">Loading your team…</p>
                )
              )}
              {!loadingMembers && filteredChurchMembers.map(m => {
                const isSelected = selectedSet.has(m.user_id);
                const role = roleLabel(m.role);
                return (
                  <button
                    key={m.user_id}
                    type="button"
                    role="checkbox"
                    aria-checked={isSelected}
                    onClick={() => {
                      setSelectedNewMembers(prev =>
                        isSelected ? prev.filter(id => id !== m.user_id) : [...prev, m.user_id]
                      );
                    }}
                    className={isStudio
                      ? `st-amrow${isSelected ? ' st-on' : ''}`
                      : `w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${
                        isSelected ? 'bg-blue-50 dark:bg-blue-950/40 ring-1 ring-blue-200' : 'hover:bg-slate-50 dark:hover:bg-white/5'
                      }`}
                    data-testid={`member-option-${m.user_id}`}
                  >
                    <span
                      aria-hidden="true"
                      className={isStudio
                        ? `st-cb${isSelected ? ' st-on' : ''}`
                        : `h-4 w-4 shrink-0 rounded-sm border flex items-center justify-center ${isSelected ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 dark:border-white/30'}`}
                    >
                      {isSelected && <Check className={isStudio ? undefined : 'h-3 w-3'} />}
                    </span>
                    <UserAvatar
                      userId={m.user_id}
                      name={m.display_name || m.user_name}
                      size="sm"
                    />
                    <div className="flex-1 min-w-0">
                      <div className={isStudio ? 'st-amname' : 'text-sm font-medium truncate flex items-center gap-1.5'}>
                        <span className={isStudio ? undefined : 'truncate'}>{m.display_name || m.user_name}</span>
                        {role && (
                          <span className={isStudio ? 'st-amrole' : 'text-[10px] font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500'}>{role}</span>
                        )}
                      </div>
                      <div className={isStudio ? 'st-amemail' : 'text-xs text-slate-400 dark:text-slate-500 truncate'}>{m.user_email}</div>
                    </div>
                  </button>
                );
              })}
              {!loadingMembers && filteredChurchMembers.length === 0 && (
                isStudio ? (
                  <div className="st-amquiet">
                    <b>{memberSearch ? 'No matching members' : 'All members are already in this group'}</b>
                    {!memberSearch && <span>Invite more people to the team first.</span>}
                  </div>
                ) : (
                  <p className="text-center text-sm text-slate-400 dark:text-slate-500 py-4">
                    {memberSearch ? 'No matching members' : 'All members are already in this group'}
                  </p>
                )
              )}
            </div>
          </ScrollArea>
          <DialogFooter data-ui="dlg-f">
            <Button variant="outline" onClick={() => setShowAddMembers(false)} data-testid="cancel-add-members-btn">Cancel</Button>
            <Button
              variant="outline"
              onClick={() => window.open('/settings/team', '_blank')}
              className="gap-1.5"
              data-testid="invite-members-btn"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              {isStudio ? 'Invite members' : 'Invite Members'}
            </Button>
            {!noAvailableMembers && (
              <Button
                onClick={handleAddMembers}
                disabled={!selectedNewMembers.length || addingMembers}
                data-testid="confirm-add-members-btn"
              >
                {addingMembers
                  ? 'Adding...'
                  : isStudio
                    ? `Add ${selectedNewMembers.length || ''} member${selectedNewMembers.length !== 1 ? 's' : ''}`.replace('  ', ' ')
                    : `Add ${selectedNewMembers.length || ''} Member${selectedNewMembers.length !== 1 ? 's' : ''}`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <div className={isStudio ? 'st-gwrap' : 'h-[calc(100vh-57px)] flex'} data-testid="groups-view">
      {/* Groups List */}
      <div className={isStudio
        ? `st-glist${selectedGroup ? ' st-gphone-hide' : ''}`
        : `${selectedGroup ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-72 border-r border-slate-200 dark:border-white/10 bg-card`}>
        {isStudio ? stListHeader() : (
        <div className="p-4 border-b border-slate-200 dark:border-white/10">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Groups</h2>
              {!introVideoDismissed && (
                <button
                  onClick={() => setShowIntroVideo(true)}
                  className="relative px-2 py-1 rounded-md bg-orange-500 text-white text-xs font-medium flex items-center gap-1 overflow-hidden"
                  style={{ isolation: 'isolate' }}
                  data-ui="howto"
                  data-testid="groups-intro-btn"
                >
                  <span
                    className="absolute inset-0 rounded-md"
                    style={{
                      background: `conic-gradient(from var(--angle), transparent 60%, #faff00 80%, transparent 100%)`,
                      animation: 'spin-snake 3s linear infinite',
                      padding: '2px',
                      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                      WebkitMaskComposite: 'xor',
                      maskComposite: 'exclude',
                    }}
                  />
                  <Play className="w-3 h-3" />
                  <span>Instructions</span>
                </button>
              )}
            </div>
            <Button size="sm" onClick={openCreateDialog} className="gap-1" data-testid="create-group-btn">
              <Plus className="w-3.5 h-3.5" />
              New
            </Button>
          </div>
        </div>
        )}

        <ScrollArea className={isStudio ? 'st-gscroll' : 'flex-1'}>
          <div className={isStudio ? 'st-glb' : 'p-2 space-y-1'}>
            {groups.map(group => (
              <button
                key={group.id}
                onClick={() => handleSelectGroup(group)}
                className={isStudio
                  ? `st-grow${selectedGroup === group.id ? ' st-on' : ''}`
                  : `w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                    selectedGroup === group.id ? 'bg-slate-100 dark:bg-[#3a3d41]' : 'hover:bg-slate-50 dark:hover:bg-white/5'
                  }`}
                data-testid={`group-item-${group.tag}`}
              >
                {isStudio ? (
                  <span className="st-gsq" aria-hidden="true"><Users /></span>
                ) : (
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: group.color }}>
                    {group.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className={isStudio ? 'st-gname' : 'font-medium text-sm text-slate-800 dark:text-slate-100 truncate'}>{group.name}</div>
                  <div className={isStudio ? 'st-gsub' : 'text-xs text-slate-400 dark:text-slate-500'}>@{group.tag} · {group.members?.length || 0} members</div>
                </div>
                <ChevronRight className={isStudio ? undefined : 'w-4 h-4 text-slate-300 flex-shrink-0'} />
              </button>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Group Detail */}
      {selectedGroup && (
        <div className={isStudio ? 'st-gdet' : 'flex-1 flex flex-col bg-slate-50 dark:bg-[#1e1f22]'}>
          {loadingDetail ? (
            isStudio ? stDetailSkeletonBody() : (
            <div className="flex-1 p-6 space-y-4">
              <div className="h-8 w-1/3 rounded-lg bg-slate-200/60 dark:bg-white/10 animate-pulse" />
              <div className="h-24 rounded-2xl bg-slate-200/60 dark:bg-white/10 animate-pulse" />
              <div className="h-24 rounded-2xl bg-slate-200/40 dark:bg-white/5 animate-pulse" />
            </div>
            )
          ) : groupDetail ? (
            <>
              <div className={isStudio ? 'st-gdh' : 'p-4 bg-card border-b border-slate-200 dark:border-white/10'}>
                <div className={isStudio ? 'st-contents' : 'flex items-center gap-3'}>
                  <button
                    onClick={() => { setSelectedGroup(null); setGroupDetail(null); }}
                    className={isStudio ? 'st-ibtn st-gback' : 'md:hidden p-1'}
                    aria-label={isStudio ? 'Back to groups' : undefined}
                  >
                    <ArrowLeft className={isStudio ? undefined : 'w-5 h-5 text-slate-600 dark:text-slate-300'} />
                  </button>
                  {isStudio ? (
                    <span className="st-gsq st-gsq-l" aria-hidden="true"><Users /></span>
                  ) : (
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: groupDetail.color }}>
                      {groupDetail.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className={isStudio ? 'st-gdtext' : 'flex-1'}>
                    <h3 className={isStudio ? 'st-gdname' : 'font-semibold text-slate-800 dark:text-slate-100'}>{groupDetail.name}</h3>
                    <p className={isStudio ? 'st-gsub' : 'text-sm text-slate-500 dark:text-slate-400'}>
                      {isStudio
                        ? `@${groupDetail.tag} · ${groupDetail.member_details?.length || 0} member${(groupDetail.member_details?.length || 0) === 1 ? '' : 's'}`
                        : `@${groupDetail.tag}`}
                    </p>
                  </div>
                  <div className={isStudio ? 'st-contents' : 'flex items-center gap-1'}>
                    <Button variant="ghost" size="icon" className={isStudio ? 'st-ibtn' : 'h-8 w-8'} onClick={() => openEditDialog(groupDetail)} data-testid="edit-group-btn">
                      <Edit className={isStudio ? undefined : 'w-4 h-4'} />
                    </Button>
                    <Button variant="ghost" size="icon" className={isStudio ? 'st-ibtn' : 'h-8 w-8 text-red-500 dark:text-red-400 hover:text-red-600'} onClick={() => { setDeletingGroup(groupDetail); setShowDeleteDialog(true); }} data-testid="delete-group-btn">
                      <Trash2 className={isStudio ? undefined : 'w-4 h-4'} />
                    </Button>
                  </div>
                </div>
              </div>

              <div className={isStudio ? 'st-gbody' : 'p-4'}>
                <div className={isStudio ? 'st-gmh' : 'flex items-center justify-between mb-3'}>
                  {isStudio ? (
                    <>
                      <b>Members</b>
                      <span className="st-count">{groupDetail.member_details?.length || 0}</span>
                      <span className="st-sp" />
                    </>
                  ) : (
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Members ({groupDetail.member_details?.length || 0})</span>
                  )}
                  <Button size="sm" variant="outline" onClick={handleOpenAddMembers} className={isStudio ? 'st-btn st-sec st-sm' : 'gap-1'} data-testid="add-members-btn">
                    <UserPlus className={isStudio ? undefined : 'w-3.5 h-3.5'} />
                    {isStudio ? (
                      <>
                        <span className="hidden md:inline">Add members</span>
                        <span className="md:hidden">Add</span>
                      </>
                    ) : 'Add'}
                  </Button>
                </div>

                {(!groupDetail.member_details || groupDetail.member_details.length === 0) ? (
                  isStudio ? (
                    <div className="st-empty" style={{ padding: '8px 20px 20px' }}>
                      <span className="st-glyph"><Users /></span>
                      <b>No members yet</b>
                      <span>Add team members to your group after they join Worship OS.</span>
                    </div>
                  ) : (
                  <Card className="border-dashed">
                    <CardContent className="py-8 text-center">
                      <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500 dark:text-slate-400">Add team members to your group after they join Worship OS</p>
                    </CardContent>
                  </Card>
                  )
                ) : isStudio ? (
                  <div className="st-gtblwrap">
                    <table className="st-gtbl">
                      <thead>
                        <tr><th>Member</th><th style={{ width: 120 }}>Role</th><th style={{ width: 44 }} /></tr>
                      </thead>
                      <tbody>
                        {groupDetail.member_details.map(member => (
                          <tr key={member.user_id}>
                            <td>
                              <span className="st-gid">
                                <UserAvatar userId={member.user_id} name={member.display_name || member.user_name} size="sm" />
                                <div>
                                  <b>{member.display_name || member.user_name}{member.user_id === user?.id ? <span className="st-gsub"> · you</span> : null}</b>
                                  <span>{member.user_email}</span>
                                </div>
                              </span>
                            </td>
                            <td className="st-grole">
                              {member.role === 'owner' ? 'Owner' : member.role === 'admin' ? 'Admin' : 'Member'}
                            </td>
                            <td className="st-gact">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="st-ibtn st-ibtn-sm"
                                aria-label="Remove from group"
                                onClick={() => handleRemoveMember(member.user_id)}
                                data-testid={`remove-member-${member.user_id}`}
                              >
                                <UserMinus />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {groupDetail.member_details.map(member => (
                      <div key={member.user_id} className="flex items-center gap-3 p-2 rounded-lg bg-card hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                        <UserAvatar
                          userId={member.user_id}
                          name={member.display_name || member.user_name}
                          size="sm"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{member.display_name || member.user_name}</div>
                          <div className="text-xs text-slate-400 dark:text-slate-500">{member.user_email}</div>
                        </div>
                        <span className="text-xs text-slate-400 dark:text-slate-500 capitalize">{member.role}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-slate-400 dark:text-slate-500 hover:text-red-500"
                          onClick={() => handleRemoveMember(member.user_id)}
                          data-testid={`remove-member-${member.user_id}`}
                        >
                          <UserMinus className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* No group selected (desktop) */}
      {!selectedGroup && (
        isStudio ? (
          <div className="st-gdet st-gcentre hidden md:flex">
            <div className="st-empty">
              <span className="st-glyph"><Users /></span>
              <b>Select a group to manage</b>
            </div>
          </div>
        ) : (
        <div className="hidden md:flex flex-1 items-center justify-center bg-slate-50 dark:bg-[#1e1f22]">
          <div className="text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-400 dark:text-slate-500 text-sm">Select a group to manage</p>
          </div>
        </div>
        )
      )}

      {/* Dialogs */}
      {renderCreateDialog()}

      {/* Add Members Dialog */}
      {renderAddMembersDialog()}

      {/* Delete Confirmation */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-sm" data-ui-dialog={isStudio ? 'confirm' : undefined}>
          <DialogHeader>
            <DialogTitle>{isStudio ? `Delete “${deletingGroup?.name || ''}”?` : 'Delete Group'}</DialogTitle>
          </DialogHeader>
          <p className={isStudio ? 'st-ghelp' : 'text-sm text-slate-600 dark:text-slate-300'}>
            {isStudio
              ? 'Posts previously tagged with this group will become visible to everyone.'
              : (
                <>
                  Are you sure you want to delete <strong>{deletingGroup?.name}</strong>? Posts previously tagged with this group will become visible to everyone.
                </>
              )}
          </p>
          <DialogFooter data-ui="dlg-f">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteGroup} data-testid="confirm-delete-group-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Intro Video Dialog */}
      {showIntroVideo && (
        <Dialog open={showIntroVideo} onOpenChange={handleDismissIntro}>
          <DialogContent className="max-w-3xl p-0 overflow-hidden">
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src="https://www.loom.com/embed/39bd17e044e54c80a3f8c02e778e73e9"
                title="Groups Instructions"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
            <div className="px-4 py-2 bg-slate-50 dark:bg-[#1e1f22] border-t border-slate-200 dark:border-white/10 text-right">
              <a
                href="https://www.loom.com/share/39bd17e044e54c80a3f8c02e778e73e9"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                data-testid="groups-intro-fallback-2"
              >
                <ExternalLink className="w-3 h-3" />
                Trouble playing? Open in Loom
              </a>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
